import { Milestone, DesignStage } from "../interfaces/Milestone";
import { EstimateData } from "../interfaces/EstimateData";
import { Estimate } from "../interfaces/Estimate";
import { fetchDataStrict } from "../clients/api";
import { Result } from "./types";
import { handleError } from "../errors";
import { CacheHandle, defineCachedLookup, defineCachedResource } from "./helpers";

type MilestoneScope = { projectId: string; milestoneId: string };

/**
 * Uncached: the light estimate record (not the export) — staleness probes must hit the server
 * fresh.
 */

export interface RowStamp {
  count: number;
  newestUpdatedAt: string | undefined;
}

const fetchEstimateMilestonesByProjectIdUncached = async (projectId: string): Promise<Result<Milestone[]>> => {
  try {
    const milestoneData = await fetchDataStrict({
      table: "milestones",
      params: { search: `milestones.project;${projectId}` },
    });
    const milestones: Milestone[] = milestoneData?.data || [];
    return { success: true, data: milestones };
  } catch (error) {
    const err = handleError(error, { table: "milestones", context: "fetchEstimateMilestonesByProjectIdUncached" });
    return { success: false, error: err };
  }
};

const fetchEstimateMilestoneUncached = async ({
  projectId,
  milestoneId,
}: MilestoneScope): Promise<Result<Milestone>> => {
  try {
    const response = await fetchDataStrict({
      table: "milestones",
      id: milestoneId,
      params: { search: `milestones.project;${projectId}` },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    const err = handleError(error, { table: "milestones", context: "fetchEstimateMilestoneUncached" });
    return { success: false, error: err };
  }
};

const milestone = defineCachedResource<MilestoneScope, Milestone>({
  scopeKey: ({ milestoneId }) => milestoneId,
  fetch: fetchEstimateMilestoneUncached,
});

const fetchEstimateMilestone = (projectId: string, milestoneId: string): Promise<Result<Milestone>> =>
  milestone.get({ projectId, milestoneId });

const fetchEstimateByIdUncached = async (estimateId: string): Promise<Result<Estimate>> => {
  try {
    const response = await fetchDataStrict({ table: "estimates", id: estimateId });
    return { success: true, data: response?.data };
  } catch (error) {
    const err = handleError(error, { table: "estimates", context: "fetchEstimateByIdUncached" });
    return { success: false, error: err };
  }
};

const fetchEstimateExportUncached = async (estimateId: string): Promise<Result<EstimateData>> => {
  try {
    const response = await fetchDataStrict({
      table: "estimates",
      id: estimateId,
      subRoute: "line_items/export",
      method: "POST",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "estimates",
      subRoute: "line_items/export",
      context: "fetchEstimateExportUncached",
    });
    return { success: false, error: err };
  }
};

const fetchRowStampUncached = async (
  table: string,
  search: string | string[],
  { projectId, params: extraParams }: { projectId?: string; params?: Record<string, string> } = {}
): Promise<Result<RowStamp>> => {
  try {
    const params = { ...extraParams, search, limit: 1, order: `${table}.updated_at`, dir: "desc" };
    const response = projectId
      ? await fetchDataStrict({ table: "projects", id: projectId, subRoute: table, params })
      : await fetchDataStrict({ table, params });
    return {
      success: true,
      data: { count: Number(response?.pagination?.count ?? 0), newestUpdatedAt: response?.data?.[0]?.updated_at },
    };
  } catch (error) {
    const err = handleError(error, { table, context: "fetchRowStampUncached" });
    return { success: false, error: err };
  }
};

const fetchUnitTypesByEstimateIdUncached = async (
  estimateId: string
): Promise<Result<Array<{ id: string; updated_at?: string }>>> => {
  try {
    const response = await fetchDataStrict({
      table: "unit_types",
      params: { search: `unit_types.estimate;${estimateId}` },
    });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, { table: "unit_types", context: "fetchUnitTypesByEstimateIdUncached" });
    return { success: false, error: err };
  }
};

const estimateExport = defineCachedResource<string, EstimateData>({
  scopeKey: (estimateId) => estimateId,
  fetch: fetchEstimateExportUncached,
});

/**
 * Fetch the export fresh and swap it into the cache on success; on failure the cached copy is left
 * intact. For QTO review builds, which need current data but must not lose it on a transient blip.
 */
export const refreshEstimateExportCache = estimateExport.refresh;

export const stageEstimateExport = estimateExport.stage;

export const watchEstimateExportForClear = estimateExport.watchForClear;

/**
 * Cached: memoized per estimateId. Cleared with the rest of `estimatePayloadCaches`.
 */
const fetchEstimateExport = estimateExport.get;

const fetchDesignStagesUncached = async (): Promise<Result<DesignStage[]>> => {
  try {
    const response = await fetchDataStrict({ table: "design_stages" });
    const designStages = response?.data || [];
    return { success: true, data: designStages };
  } catch (error) {
    const err = handleError(error, { table: "design_stages", context: "fetchDesignStages" });
    return { success: false, error: err };
  }
};

const designStages = defineCachedLookup(fetchDesignStagesUncached);

const fetchDesignStages = designStages.get;

/** The estimate's own payload: its export. */
export const estimatePayloadCaches: CacheHandle[] = [estimateExport];

/** The estimate's milestone, a lookup: a failure to load it leaves the estimate loaded. */
export const milestoneCaches: CacheHandle[] = [milestone];

export const designStageCaches: CacheHandle[] = [designStages];

export {
  fetchEstimateMilestone,
  fetchEstimateMilestonesByProjectIdUncached,
  fetchEstimateExport,
  fetchEstimateExportUncached,
  fetchEstimateMilestoneUncached,
  fetchDesignStages,
  fetchEstimateByIdUncached,
  fetchRowStampUncached,
  fetchUnitTypesByEstimateIdUncached,
};
